<script lang="ts" setup>
import { useTranslations } from '~/composables/translations';
import { createApp, onMounted, ref, watch } from 'vue';
import { requirejs } from '~/modules/require';

import MapPopup from '~/components/common/MapPopup.vue';
import { useRouter } from 'vue-router';

const router = useRouter();

// VARIABLES
let Leaflet: any;
const container = ref();
let map: any = {};
let markersLayer: any;
let geoManager: any = {};
const defaultPosition = [48.866667, 2.333333];
const defaultZoom = 12;
const scrollWheelZoom = true;
const maxZoom = 18;
const minZoom = 1;
const maxBounds = [
  [90, -180.0],
  [-90, 180.0],
];
const geolocation = false;
const geolocationOnStart = false;
const displayPosition = false;
const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
let imagePath = '';
let locationIsDisplay = false;
let markersArray: any[] = [];
const isScrollingOnMapWithoutPressingControl = ref(false);
let timeoutId: string | number | NodeJS.Timeout | null | undefined = null;
const isTactileDevice = ref(false);
let userMarker: any;
let markersList: any;

// PROPS
interface Props {
  explore: boolean;
  labsList: any[];
  selectedLabs: any[];
  customLabel?: string;
  centerHasChanged?: (...args: unknown[]) => void;
  onMapLoaded?: (...args: unknown[]) => void;
  // The map will zoom only if the user Press CTRL + Mouse Wheel
  zoomWithControlAndWheel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  explore: true,
  labsList: undefined,
  selectedLabs: undefined,
  customLabel: '',
  centerHasChanged() {},
  onMapLoaded() {},
  // The map will zoom only if the user Press CTRL + Mouse Wheel
  zoomWithControlAndWheel: true,
});

// EMITS
const emit = defineEmits(['toggleSelected']);

// NLS
const { i18n } = useTranslations('components/common/map_viewer');

// Watcher
watch(
  () => props.labsList,
  () => {
    renderLabsOnMap();
  },
  { deep: true },
);

// METHODS
const loadMap = async () => {
  await loadDependencies();
  Leaflet.Icon.Default.imagePath = imagePath;

  createMap();
  addTileLayer();
  if (geolocation) {
    await initGeolocationManager();
  } else {
    setMapPosition([0, 0], 2);
  }

  addMarkersLayer();
  renderLabsOnMap();
  openLeafletInNewTab();
};

const createMap = () => {
  map = new Leaflet.Map(container.value, {
    center: defaultPosition,
    zoom: defaultZoom,
    scrollWheelZoom: scrollWheelZoom && !props.zoomWithControlAndWheel,
    maxZoom,
    minZoom,
    maxBounds,
  })
    .on('moveend', props.centerHasChanged)
    .whenReady(props.onMapLoaded);

  if (props.zoomWithControlAndWheel) {
    map.on('mousemove', () => {
      map.getContainer().focus();
    });
    Leaflet.DomEvent.on(
      map.getContainer(),
      'wheel',
      (e: { ctrlKey: any; preventDefault: () => void; wheelDeltaY: number }) => {
        if (e.ctrlKey) {
          e.preventDefault(); // Disable browser zoom
          map.setZoom(Math.round(map.getZoom() + e.wheelDeltaY / 100));
          isScrollingOnMapWithoutPressingControl.value = false;
        } else {
          isScrollingOnMapWithoutPressingControl.value = true;
          clearTimeout(String(timeoutId)); // Keep on screen the overlay
          timeoutId = setTimeout(() => {
            isScrollingOnMapWithoutPressingControl.value = false;
          }, 500);
        }
      },
    );
  }
};

const addTileLayer = () => {
  Leaflet.tileLayer(tileUrl, {
    attribution: '3DS',
    maxZoom,
  }).addTo(map);
};

const addMarkersLayer = async () => {
  markersLayer = new Leaflet.MarkerClusterGroup({
    chunkedLoading: true,
    showCoverageOnHover: false,
  });

  map.addLayer(markersLayer);
};

const openLeafletInNewTab = () => {
  const attributionLink = document.querySelector('.leaflet-control-attribution a');

  if (attributionLink) {
    attributionLink.setAttribute('target', '_blank');
  }
};

const initGeolocationManager = async () => {
  const [MPGeolocationManager] = await requirejs<[any]>(['DS/Leaflet/MPGeolocationManager']);

  geoManager = new MPGeolocationManager();
  geoManager.setDelegate(this);
  const PositionButton = buildGeolocationControl();
  map.addControl(new PositionButton());

  if (geolocationOnStart) {
    geoManager.getCurrentLocation(updateCurrentLocation);
  } else {
    setMapPosition([0, 0], 2);
  }
};

const updateCurrentLocation = (position: { coords: { latitude?: any; longitude?: any } }) => {
  if (!position) {
    return;
  }

  if (displayPosition) {
    addLocationMarker(position.coords);
  }

  setMapPosition([position.coords.latitude, position.coords.longitude], 10);
};

const addLocationMarker = (options: any) => {
  const coords = options || {};
  const pos = new Leaflet.LatLng(coords.latitude, coords.longitude);

  if (userMarker) {
    userMarker.setLatLng(pos);
  } else {
    const userIcon = Leaflet.icon({
      iconUrl: `${imagePath}/picto_geo.png`,
      iconSize: [24, 24],
    });

    userMarker = Leaflet.marker(pos, {
      icon: userIcon,
    });
    userMarker.addTo(map);
  }

  locationIsDisplay = true;
};

const buildGeolocationControl = () =>
  Leaflet.ControLeaflet.extend({
    options: {
      position: 'topleft',
      callback() {
        let shouldBeRemove;
        let pos;

        if (geoManager) {
          shouldBeRemove = true;

          if (userMarker) {
            pos = userMarker.getLatLng();
            shouldBeRemove = map.getBounds().contains(pos);
          }

          if (locationIsDisplay && shouldBeRemove) {
            removeLocationMarker();
          } else {
            geoManager.getCurrentLocation(updateCurrentLocation);
          }
        }
      },
    },
    onAdd() {
      const container = Leaflet.DomUtiLeaflet.create(
        'div',
        'leaflet-bar leaflet-control leaflet-control-custom',
      );

      const link = Leaflet.DomUtiLeaflet.create(
        'a',
        'leaflet-bar-part leaflet-bar-part-single',
        container,
      );
      link.href = '#';
      this._icon = Leaflet.DomUtiLeaflet.create(
        'span',
        'fonticon fonticon-location locate-icon',
        link,
      );
      Leaflet.DomEvent.on(link, 'click', this.options.callback, this)
        .on(link, 'click', Leaflet.DomEvent.stopPropagation)
        .on(link, 'click', Leaflet.DomEvent.preventDefault)
        .on(link, 'dblclick', Leaflet.DomEvent.stopPropagation);

      return container;
    },
  });

const removeLocationMarker = () => {
  if (userMarker) {
    map.removeLayer(userMarker);
    userMarker = null;
  }

  locationIsDisplay = false;
};

const setMapPosition = (positions: any[], zoom: number) => {
  if (!positions || !map) {
    return;
  }

  if (map) {
    map.setView(positions, zoom || defaultZoom);
  }
};

const renderLabsOnMap = () => {
  markersArray = [];
  if (
    !map ||
    !props.labsList ||
    !markersLayer ||
    !Object.keys(markersLayer).length ||
    !Object.keys(map).length
  ) {
    return;
  }

  markersList = {};

  props.labsList.forEach((lab) => {
    addLabOnMap(lab);
  });
  markersLayer.clearLayers();
  map.removeLayer(markersLayer);
  markersLayer = null;
  markersLayer = new Leaflet.MarkerClusterGroup({
    chunkedLoading: true,
    showCoverageOnHover: false,
  });

  map.addLayer(markersLayer);
  markersLayer.addLayers(markersArray);
};

const addLabOnMap = async (lab: {
  latitude: null;
  longitude: null;
  id: string | number;
  storeURL: string;
}) => {
  if (lab.latitude === null || lab.longitude === null) {
    return;
  }

  const icon = new Leaflet.Icon.Default();

  icon.options.className = `icon-${lab.id}`;
  const labMarker = Leaflet.marker([lab.latitude, lab.longitude], {
    icon,
  });

  const component = createApp(MapPopup, {
    lab,
    explore: props.explore,
    selectedLabs: props.selectedLabs,
    customLabel: props.customLabel,
    labLink: router.resolve({ name: 'Showroom', params: { id: lab.storeURL } }).href,
    onToggleSelected: (id: string) => emit('toggleSelected', id),
  });

  const el = document.createElement('div');
  component.mount(el);

  labMarker.bindPopup(el);
  labMarker.on('click', labMarker.openPopup);
  labMarker.on('dblclick ', (e: { latlng: any }) => {
    map.setView(e.latlng, 12);
  });

  markersArray.push(labMarker);
  markersList[lab.id] = labMarker;
};

const updateMap = () => {
  if (map) {
    map._onResize();
  }
};

const setView = (pos: any[], zoom: number) => {
  if (map && map?.setView) {
    map?.setView(pos, zoom);
  }
};

const loadDependencies = async () => {
  const [WebappsUtils, LeafletModule] = await requirejs<[any, any]>([
    'WebappsUtils/WebappsUtils',
    'DS/Leaflet/LeafletLoader',
    'DS/Leaflet/MarkerClusterLoader',
    'css!DS/Leaflet/Leaflet',
    'css!DS/Leaflet/assets/plugin/MarkerCluster/MarkerCluster.Default',
    'css!DS/Leaflet/assets/plugin/MarkerCluster/MarkerCluster',
    'css!DS/MP3DPCommonComponents/MP3DPCommonComponents',
  ]);

  imagePath = `${WebappsUtils.getWebappsBaseUrl()}Leaflet/assets/images`;
  Leaflet = LeafletModule;
};

onMounted(() => {
  isTactileDevice.value =
    typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
});

// EXPOSE METHODS
defineExpose({
  loadMap,
  updateMap,
  setMapPosition,
  renderLabsOnMap,
  markersList,
  map,
  setView,
});
</script>

<template>
  <div ref="labsMapContainer" class="labs-map-container" style="position: relative">
    <Transition name="fade">
      <div
        v-if="!isTactileDevice"
        v-show="isScrollingOnMapWithoutPressingControl"
        class="flex map-overlay pa-30px text-center"
      >
        {{ i18n.scrollOverlayText }}
      </div>
    </Transition>
    <div ref="mp3dpMap" class="mp3dp-map">
      <div ref="container" class="map-container" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;
@forward '/assets/scss/skeletons';

.labs-map-container {
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.6s ease-in-out;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .map-overlay {
    pointer-events: none;
    position: absolute;
    background-color: rgba(255, 255, 255, 0.5);
    top: 0%;
    left: 0%;
    z-index: 5;
    font-size: 2vw;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;

    // No overlay on mobile
    @include variables.for-xs {
      display: none;
    }
  }
}
</style>
